import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { PageHeader, SectionCard, KpiCard, Money, Loading, EmptyState } from "@/components/common/ui";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ChartTooltip } from "@/components/charts/ChartTooltip";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Wallet, Receipt, Layers, Gauge } from "lucide-react";

const fmtCount = (n) => new Intl.NumberFormat("en-IN").format(n || 0);
const pctOf = (v, t) => (t ? Math.round((v / t) * 100) : 0);
const spendDate = (r) => r.invoice_date || r.date || r.spend_month || "";

// Fiscal-year month order (Apr → Mar) so the chart reads left-to-right.
const FY_ORDER = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const monthRank = (m) => {
  const idx = FY_ORDER.indexOf(String(m).slice(0, 3));
  return idx < 0 ? 99 : idx;
};

// Utilization pill — green within plan, amber near limit, red over budget.
function UtilBadge({ pct }) {
  const tone = pct > 100
    ? "border-[hsl(var(--danger)/0.3)] bg-[hsl(var(--danger)/0.1)] text-[hsl(var(--danger))]"
    : pct >= 85
      ? "border-[hsl(var(--warning)/0.3)] bg-[hsl(var(--warning)/0.12)] text-[hsl(var(--warning))]"
      : "border-[hsl(var(--success)/0.3)] bg-[hsl(var(--success)/0.1)] text-[hsl(var(--success))]";
  return <Badge variant="outline" className={`text-[11px] font-semibold tabular-nums ${tone}`}>{pct}%</Badge>;
}

export default function VerticalLeadDashboard() {
  const { user, assignedVerticals } = useAuth();
  const { money } = useCurrency();
  const [spends, setSpends] = useState(null);
  const [budgets, setBudgets] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    setLoading(true);
    Promise.all([
      api.listSpends().catch(() => []),
      api.plannedBudgets().catch(() => ({ rows: [] })),
    ]).then(([sp, pb]) => {
      if (!on) return;
      setSpends(Array.isArray(sp) ? sp : []);
      setBudgets(Array.isArray(pb?.rows) ? pb.rows : []);
    }).finally(() => on && setLoading(false));
    return () => { on = false; };
  }, []);

  const agg = useMemo(() => {
    const rows = spends || [];
    const brows = budgets || [];
    const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const planned = brows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const vendors = new Set(rows.map((r) => r.vendor).filter(Boolean));

    // Month → { spend, count } for the lead's vertical(s).
    const mm = {};
    for (const r of rows) {
      const m = r.spend_month || "—";
      const e = (mm[m] = mm[m] || { month: m, spend: 0, count: 0 });
      e.spend += Number(r.amount) || 0;
      e.count += 1;
    }
    const monthly = Object.values(mm)
      .map((m) => ({ ...m, pct: pctOf(m.spend, total) }))
      .sort((a, b) => monthRank(a.month) - monthRank(b.month));
    const peak = monthly.reduce((mx, m) => (m.spend > mx.spend ? m : mx), { spend: 0, month: "—" });

    const recent = [...rows].sort((a, b) => String(spendDate(b)).localeCompare(String(spendDate(a)))).slice(0, 8);
    return {
      total, planned, remaining: planned - total, util: pctOf(total, planned),
      count: rows.length, vendors: vendors.size, activeMonths: monthly.length,
      monthly, peak, recent,
    };
  }, [spends, budgets]);

  const vLabel = (assignedVerticals || []).join(" · ") || "your vertical";

  return (
    <div className="space-y-5" data-testid="vertical-lead-dashboard">
      <PageHeader
        title={`Welcome, ${user?.name || "Vertical Lead"}`}
        description={`Monthly spend incurred for ${vLabel} — your vertical only.`}
      >
        <div className="flex flex-wrap gap-1.5" data-testid="vl-vertical-badges">
          {(assignedVerticals || []).map((v) => (
            <Badge key={v} variant="outline" className="border-[hsl(var(--primary)/0.3)] bg-[hsl(var(--primary)/0.08)] text-xs font-medium text-primary">{v}</Badge>
          ))}
        </div>
      </PageHeader>

      {loading ? (
        <Loading rows={5} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4" data-testid="vl-kpis">
            <KpiCard testId="vl-kpi-planned" label="Planned budget" accent="blue" icon={Layers}
              value={<Money value={agg.planned} />} sub={`Allocated to ${vLabel}`} />
            <KpiCard testId="vl-kpi-actual" label="Actual spend" accent="teal" icon={Wallet}
              value={<Money value={agg.total} />} sub={agg.remaining >= 0 ? `${money(agg.remaining)} remaining` : `${money(-agg.remaining)} over plan`} />
            <KpiCard testId="vl-kpi-util" label="Utilization" accent={agg.util > 100 ? "red" : agg.util >= 85 ? "amber" : "green"} icon={Gauge}
              value={`${agg.util}%`} sub="Actual vs planned" progress={agg.planned ? agg.total / agg.planned : 0} />
            <KpiCard testId="vl-kpi-count" label="Invoices logged" accent="slate" icon={Receipt}
              value={fmtCount(agg.count)} sub={`${fmtCount(agg.vendors)} vendors · ${fmtCount(agg.activeMonths)} months`} />
          </div>

          {agg.count === 0 ? (
            <SectionCard title="Nothing to show yet" testId="vl-empty">
              <div className="p-6"><EmptyState title="No invoices for your vertical yet" message="Invoices you log from now onwards will appear here, broken down by month." /></div>
            </SectionCard>
          ) : (
            <>
              <SectionCard title="Monthly spend incurred"
                description={`Spend logged each month for ${vLabel}. Peak: ${agg.peak.month} (${money(agg.peak.spend)}).`}
                testId="vl-monthly-chart">
                <div className="p-4" style={{ height: 300 }} data-testid="vl-monthly-bars">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={agg.monthly} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} interval={0} />
                      <YAxis tickFormatter={(v) => money(v)} tick={{ fontSize: 11 }} width={72} tickLine={false} axisLine={false} />
                      <Tooltip cursor={{ fill: "hsl(var(--muted)/0.45)" }} content={<ChartTooltip formatter={(v) => money(v)} />} />
                      <Bar dataKey="spend" name="Spend" radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false}>
                        {agg.monthly.map((m) => (
                          <Cell key={m.month} fill={m.month === agg.peak.month ? "hsl(var(--primary))" : "hsl(var(--primary)/0.55)"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>

              <SectionCard title="Monthly breakdown" description="Spend and invoice count per month." testId="vl-monthly-table-card">
                <div className="table-scroll overflow-x-auto">
                  <Table data-testid="vl-monthly-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs font-semibold uppercase tracking-wide">Month</TableHead>
                        <TableHead className="text-right text-xs font-semibold uppercase tracking-wide">Spend</TableHead>
                        <TableHead className="text-right text-xs font-semibold uppercase tracking-wide">Invoices</TableHead>
                        <TableHead className="text-right text-xs font-semibold uppercase tracking-wide">Share</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {agg.monthly.map((m, i) => (
                        <TableRow key={m.month} data-testid={`vl-month-row-${i}`}>
                          <TableCell className="text-sm font-medium">{m.month}</TableCell>
                          <TableCell className="num text-right text-sm font-semibold tabular-nums"><Money value={m.spend} /></TableCell>
                          <TableCell className="text-right text-sm tabular-nums text-muted-foreground">{fmtCount(m.count)}</TableCell>
                          <TableCell className="text-right text-sm tabular-nums text-muted-foreground">{m.pct}%</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="border-t-2 bg-[hsl(var(--muted)/0.35)] hover:bg-[hsl(var(--muted)/0.35)]" data-testid="vl-month-total">
                        <TableCell className="text-sm font-bold">Total ({vLabel})</TableCell>
                        <TableCell className="num text-right text-sm font-bold tabular-nums"><Money value={agg.total} /></TableCell>
                        <TableCell className="text-right text-sm font-bold tabular-nums">{fmtCount(agg.count)}</TableCell>
                        <TableCell className="text-right text-sm font-bold tabular-nums">100%</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </SectionCard>

              <SectionCard title="Recent invoices" description="Your latest logged spends." testId="vl-recent">
                {agg.recent.length === 0 ? (
                  <div className="p-6"><EmptyState title="No invoices yet" message="Log an invoice from the Spends Log to see it here." /></div>
                ) : (
                  <div className="table-scroll overflow-x-auto">
                    <Table data-testid="vl-recent-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs font-semibold uppercase tracking-wide">Month</TableHead>
                          <TableHead className="text-xs font-semibold uppercase tracking-wide">Project</TableHead>
                          <TableHead className="text-xs font-semibold uppercase tracking-wide">Vertical</TableHead>
                          <TableHead className="text-xs font-semibold uppercase tracking-wide">Vendor</TableHead>
                          <TableHead className="text-right text-xs font-semibold uppercase tracking-wide">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {agg.recent.map((r, i) => (
                          <TableRow key={r.id || i} data-testid={`vl-recent-row-${i}`}>
                            <TableCell className="text-xs text-muted-foreground">{r.spend_month || "—"}</TableCell>
                            <TableCell className="text-sm font-medium">{r.project || "—"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{r.vertical}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{r.vendor || "—"}</TableCell>
                            <TableCell className="num text-right text-sm font-semibold tabular-nums"><Money value={Number(r.amount) || 0} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </SectionCard>
            </>
          )}
        </>
      )}
    </div>
  );
}
